import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { IsAccommodationAvailableRawResponse } from '@/scopes/prices/infrastructure/availability/types'
import defaultFetch from 'isomorphic-unfetch'

interface AvailabilityContext {
  accoId: string
  contextItemId: string
  bookingGateId: string
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer<AvailabilityContext>
}

const fetchOptions: RequestInit = {
  referrerPolicy: 'no-referrer-when-downgrade',
  credentials: 'include',
  headers: {
    Accept: 'application/json',
  },
}

export interface IsAccoAvailableApiClient {
  isAccommodationAvailable: (criteria: AvailabilityContext) => Promise<{
    isAccommodationAvailable: boolean
    isFlightOnly: boolean
    transportTypes?: undefined
  } | {
    isAccommodationAvailable: boolean
    isFlightOnly: boolean
    transportTypes: string[]
  }>
}

export function createAvailabilityApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
  } = dependencies as Dependencies

  async function isAccommodationAvailable(criteria: AvailabilityContext) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)

    if (response.status !== 200) {
      return {
        isAccommodationAvailable: false,
        isFlightOnly: false,
      }
    }

    const { data } = (await response.json()) as IsAccommodationAvailableRawResponse

    return {
      isAccommodationAvailable: data.isAccommodationAvailable,
      isFlightOnly: data.isFlightOnly,
      transportTypes: data.transportTypes,
    }
  }

  function buildAPIUrl({ accoId, contextItemId, bookingGateId }: AvailabilityContext) {
    const availabilityContext = {
      accoId,
      contextItemId,
      bookingGateId,
    }

    const queryString = queryStringSerializer.serialize({
      ...availabilityContext,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingGate/IsAccommodationAvailable?${queryString}`
  }

  return { isAccommodationAvailable }
}
